// ActiveFilterSummary — single-line summary above the table when the
// user has narrowed the view enough that they might lose track of
// what's currently filtered out. Renders only when 3+ "signals" are
// active (each chip group with at least one selected option counts
// as one signal; a non-empty search counts as one signal). A clear-
// all button resets every chip + the search in one click.
//
// The 3+ threshold avoids adding chrome for trivially narrow views
// (1-2 signals already render the chip-bar's own count badges) but
// surfaces a single
// reassuring summary when the table is heavily narrowed.

import type {
  DueFilterValue,
  FilterChipBarFilters,
  RagFilterValue,
  StatusFilterValue,
} from "./FilterChipBar";
import { UNATTACHED_TOKEN, UNLINKED_MILESTONE_TOKEN } from "./FilterChipBar";
import type { SummaryFilterKey } from "./applySummaryFilter";
import type { Milestone, Sow } from "../../../../types";

export interface ActiveFilterSummaryProps {
  filters: FilterChipBarFilters;
  search: string;
  onClearAll: () => void;
  /** F5: SummaryStrip card filter is the third pathway alongside
   *  chip filters + search. Surfacing it here means a single card
   *  click also triggers the Clear-all affordance, not just chip /
   *  search combinations. */
  summaryFilter?: SummaryFilterKey | null;
  /** SOWs on the parent contract — used to render human-readable
   *  labels for active SOW chips. Optional: when omitted, ids show
   *  raw, which is acceptable for a summary chip. */
  sows?: readonly Sow[];
  /** Milestones on the parent contract — used to render human-
   *  readable labels for active milestone chips. Optional: when
   *  omitted, ids show raw, which is acceptable for a summary chip. */
  milestones?: readonly Milestone[];
}

const SUMMARY_FILTER_LABEL: Record<SummaryFilterKey, string> = {
  overdue: "Overdue",
  "due-this-week": "Due this week",
  "has-failed-acceptance": "Has failed acceptance",
};

const STATUS_LABELS: Record<StatusFilterValue, string> = {
  "not-started": "Not started",
  submitted: "Submitted",
  "under-review": "Under review",
  "revision-requested": "Revision requested",
  "conditionally-accepted": "Cond. accepted",
  "awaiting-resubmission": "Awaiting resub.",
  accepted: "Accepted",
  rejected: "Rejected",
  closed: "Closed",
  overdue: "Overdue",
  late: "Late",
};

const OWNER_LABELS = {
  supplier: "Supplier",
  customer: "Customer",
  joint: "Joint",
} as const;

const RAG_LABELS: Record<RagFilterValue, string> = {
  red: "Red",
  amber: "Amber",
  green: "Green",
  "not-set": "Not set",
};

const DUE_LABELS: Record<DueFilterValue, string> = {
  overdue: "Overdue",
  "this-week": "This week",
  "next-30-days": "Next 30 days",
  future: "Future",
};

function joinList<T extends string>(
  values: T[],
  labels: Record<T, string>,
): string {
  return values.map((v) => labels[v]).join(", ");
}

function signalCount(
  filters: FilterChipBarFilters,
  search: string,
  summaryFilter: SummaryFilterKey | null | undefined,
): number {
  let count = 0;
  if (filters.status.length > 0) count += 1;
  if (filters.owner.length > 0) count += 1;
  if (filters.rag.length > 0) count += 1;
  if (filters.due.length > 0) count += 1;
  if (filters.sow.length > 0) count += 1;
  if (filters.milestone.length > 0) count += 1;
  if (filters.includeArchived) count += 1;
  if (filters.hasFailedAcceptance) count += 1;
  if (filters.notAccepted) count += 1;
  if (search.trim().length > 0) count += 1;
  if (summaryFilter) count += 1;
  return count;
}

function sowLabels(
  selected: readonly string[],
  sows: readonly Sow[] | undefined,
): string {
  return selected
    .map((token) => {
      if (token === UNATTACHED_TOKEN) return "Unattached";
      const s = sows?.find((x) => x.id === token);
      return s ? s.number : token;
    })
    .join(", ");
}

function milestoneLabels(
  selected: readonly string[],
  milestones: readonly Milestone[] | undefined,
): string {
  return selected
    .map((token) => {
      if (token === UNLINKED_MILESTONE_TOKEN) return "Unlinked";
      const m = milestones?.find((x) => x.id === token);
      if (!m) return token;
      return typeof m.milestoneNumber === "number"
        ? `${m.milestoneNumber}${m.milestoneSuffix ?? ""} · ${m.name}`
        : m.name;
    })
    .join(", ");
}

export default function ActiveFilterSummary({
  filters,
  search,
  onClearAll,
  summaryFilter,
  sows,
  milestones,
}: ActiveFilterSummaryProps) {
  const count = signalCount(filters, search, summaryFilter);
  const trimmedSearch = search.trim();
  // Render whenever ANY filter or search is active.
  // The earlier "3+ signals" threshold meant a single chip click
  // produced no Clear-all affordance — users had to click the chip
  // again (or each chip individually) to reset. Drop the threshold;
  // any narrowing surfaces the summary + Clear-all.
  if (count < 1 && trimmedSearch.length === 0) return null;

  const parts: string[] = [];
  if (summaryFilter) {
    parts.push(`Card: ${SUMMARY_FILTER_LABEL[summaryFilter]}`);
  }
  if (filters.status.length > 0) {
    parts.push(`Status: ${joinList(filters.status, STATUS_LABELS)}`);
  }
  if (filters.owner.length > 0) {
    parts.push(`Owner: ${joinList(filters.owner, OWNER_LABELS)}`);
  }
  if (filters.rag.length > 0) {
    parts.push(`RAG: ${joinList(filters.rag, RAG_LABELS)}`);
  }
  if (filters.due.length > 0) {
    parts.push(`Due: ${joinList(filters.due, DUE_LABELS)}`);
  }
  if (filters.sow.length > 0) {
    parts.push(`SOW: ${sowLabels(filters.sow, sows)}`);
  }
  if (filters.milestone.length > 0) {
    parts.push(`Milestone: ${milestoneLabels(filters.milestone, milestones)}`);
  }
  if (filters.includeArchived) {
    parts.push("Include archived");
  }
  if (filters.hasFailedAcceptance) {
    parts.push("Has failed acceptance");
  }
  if (filters.notAccepted) {
    parts.push("Not accepted");
  }
  if (trimmedSearch) {
    parts.push(`Search: '${trimmedSearch}'`);
  }

  return (
    <div
      data-testid="active-filter-summary"
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-caption text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-300"
    >
      <span>{parts.join(" · ")}</span>
      <button
        type="button"
        onClick={onClearAll}
        className="text-caption font-medium text-brand-600 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-brand-400"
      >
        Clear all
      </button>
    </div>
  );
}
